import type { ReactElement } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import type { OnboardingTask } from '@limo/database'
import { useOnboardingRun } from '@/hooks/useOnboarding'
import {
  ONBOARDING_STEPS,
  type OnboardingStepGroup,
} from '@/lib/onboarding/steps'
import { OnboardingChecklist } from '@/components/onboarding/OnboardingChecklist'
import { StepCard } from '@/components/onboarding/StepCard'
import { StripeConnectCard } from '@/components/onboarding/StripeConnectCard'
import { StripePlatformWebhookCard } from '@/components/onboarding/StripePlatformWebhookCard'
import { LimoAnywhereCard } from '@/components/onboarding/LimoAnywhereCard'
import { GoHighLevelCard } from '@/components/onboarding/GoHighLevelCard'
import { ZapierCard } from '@/components/onboarding/ZapierCard'
import { Ga4PropertyCard } from '@/components/onboarding/Ga4PropertyCard'
import { GoogleAdsCard } from '@/components/onboarding/GoogleAdsCard'
import { ConversionLabelsCard } from '@/components/onboarding/ConversionLabelsCard'
import { MicrosoftUetCard } from '@/components/onboarding/MicrosoftUetCard'
import { StapeCard } from '@/components/onboarding/StapeCard'
import { GtmServerUrlCard } from '@/components/onboarding/GtmServerUrlCard'
import { DomainCard } from '@/components/onboarding/DomainCard'
import { VerifyAdsActiveCard } from '@/components/onboarding/VerifyAdsActiveCard'
import { SmokeTestCard } from '@/components/onboarding/SmokeTestCard'

// Domain leads so the client's DNS records go out first and propagation
// runs concurrently with the rest of the onboarding.
const GROUP_ORDER: OnboardingStepGroup[] = [
  'Domain',
  'Attribution',
  'Integrations',
  'Verify',
]

/**
 * A pending stand-in for a registry step that has no task row yet — i.e. a step
 * added to the registry AFTER this run was seeded. Without it, newly-added steps
 * are invisible to in-flight runs. The edge function's getOrCreateTask creates
 * the real row on first invoke, and the 5s poll then replaces this placeholder.
 */
function placeholderTask(step: string, runId: string): OnboardingTask {
  return {
    id: `placeholder-${step}`,
    run_id: runId,
    step,
    status: 'pending',
    params: {},
    result: {},
    depends_on: [],
    error: null,
    attempts: 0,
    updated_at: '',
  }
}

interface StepCardRendererProps {
  task: OnboardingTask
  accountId: string
  runId: string
  /** All tasks in the run — for cards (e.g. DomainCard) that read sibling rows. */
  allTasks: OnboardingTask[]
}

/**
 * Registry of implemented step cards. Steps without an entry render the
 * generic stub card — each vertical slice adds its component here.
 */
const CARD_COMPONENTS: Record<
  string,
  (props: StepCardRendererProps) => ReactElement
> = {
  stripe_connect: (props) => <StripeConnectCard {...props} />,
  stripe_platform_webhook: (props) => <StripePlatformWebhookCard {...props} />,
  limo_anywhere: (props) => <LimoAnywhereCard {...props} />,
  // One GoHighLevelCard (rendered for ghl_discovery) drives both GHL task rows;
  // ghl_config renders nothing of its own (its status still shows in the
  // checklist) to avoid a duplicate card mid-flow.
  ghl_discovery: (props) => <GoHighLevelCard {...props} />,
  ghl_config: () => <></>,
  zapier_link: (props) => <ZapierCard {...props} />,
  ga4_property: (props) => <Ga4PropertyCard {...props} />,
  ads_conversions: (props) => <GoogleAdsCard {...props} />,
  conversion_labels: (props) => <ConversionLabelsCard {...props} />,
  microsoft_uet: (props) => <MicrosoftUetCard {...props} />,
  // One StapeCard (rendered for stape_domain) drives all three Stape rows via a
  // single provision action; stape_loader/stape_settings show status only.
  stape_domain: (props) => <StapeCard {...props} />,
  stape_loader: () => <></>,
  stape_settings: () => <></>,
  gtm_server_url: (props) => <GtmServerUrlCard {...props} />,
  // One DomainCard (rendered for vercel_domain) drives all three Domain rows
  // (Add domain → Generate email → live verify table); generate_dns_email and
  // verify_dns show status only.
  vercel_domain: (props) => <DomainCard {...props} />,
  generate_dns_email: () => <></>,
  verify_dns: () => <></>,
  verify_ads_active: (props) => <VerifyAdsActiveCard {...props} />,
  smoke_test: (props) => <SmokeTestCard {...props} />,
}

export function OnboardingRunPage() {
  const { runId } = useParams<{ runId: string }>()
  const { data, isLoading } = useOnboardingRun(runId)

  if (isLoading || !data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
          <Skeleton className="h-96" />
          <div className="space-y-4">
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </div>
        </div>
      </div>
    )
  }

  const { run, tasks } = data
  const taskByStep = new Map(tasks.map((task) => [task.step, task]))

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/admin/onboarding"
          className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          All onboarding runs
        </Link>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">
            {run.accounts?.name ?? 'Onboarding run'}
          </h1>
          <Badge variant="outline">{run.status}</Badge>
        </div>
        <p className="text-muted-foreground">
          Started{' '}
          {run.created_at ? new Date(run.created_at).toLocaleString() : '—'}
        </p>
      </div>

      <div className="grid items-start gap-6 lg:grid-cols-[280px_1fr]">
        <div className="lg:sticky lg:top-20">
          <OnboardingChecklist tasks={tasks} />
        </div>

        <div className="space-y-8">
          {GROUP_ORDER.map((group) => {
            const steps = ONBOARDING_STEPS.filter((s) => s.group === group)
            if (steps.length === 0) return null
            return (
              <section key={group} className="space-y-4">
                <h2 className="text-lg font-semibold">{group}</h2>
                {steps.map((step) => {
                  const task =
                    taskByStep.get(step.key) ?? placeholderTask(step.key, run.id)
                  const renderCard = CARD_COMPONENTS[step.key]
                  if (renderCard) {
                    return (
                      <div key={step.key}>
                        {renderCard({
                          task,
                          accountId: run.account_id,
                          runId: run.id,
                          allTasks: tasks,
                        })}
                      </div>
                    )
                  }
                  return <StepCard key={step.key} task={task} />
                })}
              </section>
            )
          })}
        </div>
      </div>
    </div>
  )
}
